import type { Matrix } from '../geometry/matrix';

/**
 * Canonical project document (docs/09). This application-owned structure — not a
 * renderer's private format — is the source of truth (ADR 0002). World and printed
 * dimensions are millimeters; path coordinates are local and compose into world mm.
 */
export const CURRENT_SCHEMA_VERSION = 2 as const;

export const NAIL_SHAPE_IDS = [
  'round',
  'oval',
  'almond',
  'square',
  'squoval',
  'coffin',
  'stiletto',
] as const;
export type NailShapeId = (typeof NAIL_SHAPE_IDS)[number];

export const FINGER_IDS = ['thumb', 'index', 'middle', 'ring', 'little'] as const;
export type FingerId = (typeof FINGER_IDS)[number];

export const HANDS = ['left', 'right'] as const;
export type Hand = (typeof HANDS)[number];

export type LineCap = 'round' | 'butt' | 'square';
export type LineJoin = 'round' | 'miter' | 'bevel';
/**
 * `fixed-mm`: the printed width never changes when art is resized, placed, or laddered.
 * `scale-with-design`: the width follows resizing by the geometric-mean factor
 * `sqrt(|det A|)` (ADR 0006).
 */
export type StrokeScaleMode = 'fixed-mm' | 'scale-with-design';

export interface CatalogSource {
  readonly catalogId: string;
  readonly version: string;
  readonly sha256: string;
}

export interface StrokeStyle {
  readonly color: string;
  /** Width in millimeters in design space (ADR 0006). */
  readonly widthMm: number;
  readonly opacity: number;
  readonly lineCap: LineCap;
  readonly lineJoin: LineJoin;
  readonly scaleMode: StrokeScaleMode;
}

/**
 * How a filled path decides what is inside: `evenodd` cuts a hole wherever subpaths
 * overlap (traced pictures draw holes as extra subpaths); `nonzero` fills overlaps.
 */
export type FillRule = 'nonzero' | 'evenodd';

/** Solid paint inside a closed shape (format 2, ADR 0016). */
export interface FillStyle {
  readonly color: string;
  readonly opacity: number;
  readonly rule: FillRule;
}

interface NodeBase {
  readonly id: string;
  readonly name: string;
  /** Local-to-parent affine transform. */
  readonly transform: Matrix;
  readonly visible: boolean;
  readonly locked: boolean;
  readonly source?: CatalogSource;
}

/** A path draws a line, a filled shape, or both — never neither (ADR 0016). */
export interface PathNode extends NodeBase {
  readonly type: 'path';
  /** Supported SVG path data in local coordinates. */
  readonly d: string;
  readonly fill: FillStyle | 'none';
  readonly stroke: StrokeStyle | 'none';
}

export interface GroupNode extends NodeBase {
  readonly type: 'group';
  /** Stacking order from back to front. */
  readonly children: readonly VectorNode[];
}

export type VectorNode = PathNode | GroupNode;

export interface Artboard {
  readonly widthMm: number;
  readonly heightMm: number;
}

export interface Design {
  readonly id: string;
  readonly name: string;
  readonly artboard: Artboard;
  /** Root nodes, back to front. */
  readonly nodes: readonly VectorNode[];
}

export interface NailShape {
  readonly id: NailShapeId | 'custom';
  readonly version: string;
  /** Closed silhouette; tip toward y = 0 and cuticle toward y = 1 once normalized. */
  readonly normalizedPathD: string;
}

export type NailFinish = 'matte' | 'gloss';

export interface NailInstance {
  readonly id: string;
  readonly hand: Hand;
  readonly finger: FingerId;
  readonly designId: string;
  readonly shape: NailShape;
  readonly widthMm: number;
  readonly lengthMm: number;
  /** Maps design artboard millimeters to this nail's millimeters. */
  readonly placement: Matrix;
  readonly baseColor: string;
  readonly finish: NailFinish;
}

export interface ProjectDocument {
  readonly schemaVersion: typeof CURRENT_SCHEMA_VERSION;
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Monotonic committed revision used for optimistic save checks. */
  readonly revision: number;
  readonly designs: readonly Design[];
  readonly nails: readonly NailInstance[];
  readonly notes?: string;
}

/** Engineering limits (docs/10). Tune against real projects; they are not browser limits. */
export const DOCUMENT_LIMITS = {
  maxDesigns: 1000,
  maxNails: 10,
  maxNodesPerContainer: 10_000,
  maxNestingDepth: 16,
  maxTotalNodes: 50_000,
  maxPathDataLength: 500_000,
  maxPathCommands: 20_000,
  maxTotalPathCommands: 1_000_000,
  maxNotesLength: 20_000,
  maxNameLength: 200,
  maxArtboardMm: 1000,
  minNailWidthMm: 4,
  maxNailWidthMm: 40,
  minNailLengthMm: 5,
  maxNailLengthMm: 80,
  maxStrokeWidthMm: 10,
} as const;

export const ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,127}$/;
export const COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
